import { useEffect, useState } from 'react';
import { Card, CardContent, ErrorBanner, Spinner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import type { IMetricThresholdSet, IPortfolioEpicCompletionResponse } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { useCardWidth } from '@/hooks/use-card-width';
import { useMetricDefinition } from '@/hooks/use-metric-definition';
import {
  EpicCompletionChart,
  EpicHeaderStats,
  computeEpicSummary,
} from '@/components/shared/epic-completion-chart';
import { EpicCompletionList } from './EpicCompletionList';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { formatPeriodLabel } from '@/lib/metric-view/period-label';
import { getTierColorClass } from '@/lib/metric-color-class';
import { cn } from '@/lib/utils';

interface IEpicCompletionCardProps {
  filters: IMetricViewFilters;
}

type EpicVariant = 'miniList' | 'list' | 'compactChart' | 'chart';

function pickVariant(width: number): EpicVariant {
  if (width === 0) return 'chart';
  if (width < CARD_WIDTH_THRESHOLDS.epic.miniList) return 'miniList';
  if (width < CARD_WIDTH_THRESHOLDS.epic.list) return 'list';
  if (width < CARD_WIDTH_THRESHOLDS.epic.compactChart) return 'compactChart';
  return 'chart';
}

export function EpicCompletionCard({ filters }: Readonly<IEpicCompletionCardProps>) {
  const [data, setData] = useState<IPortfolioEpicCompletionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const variant = pickVariant(width);
  // Source the linkage colour boundaries from the Epic Linkage metric
  // definition so this surface and the Workflow Hygiene Epic Linkage card
  // (PR 5) cannot drift apart when an admin edits the thresholds.
  const epicLinkageDefinition = useMetricDefinition('epicLinkage');
  const linkageThresholdSet: IMetricThresholdSet | null = epicLinkageDefinition
    ? {
        metricKey: epicLinkageDefinition.metricKey,
        direction: epicLinkageDefinition.direction,
        tiers: epicLinkageDefinition.tiers,
      }
    : null;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    portfolioApi.getEpicCompletion({
      from: filters.from,
      to: filters.to,
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
      includeArchived: filters.includeArchived || undefined,
    }).then((res) => {
      if (controller.signal.aborted) return;
      setData(res);
      setLoading(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load epic completion.');
      setLoading(false);
    });

    return () => controller.abort();
  }, [filters.from, filters.to, filters.productId, filters.teamId, filters.includeArchived]);

  const summary = data ? computeEpicSummary(data.epics) : null;
  const hasData = data && data.epics.length > 0;
  // Narrow card variants (miniList / list) drop the new "Done this period"
  // chip to keep the header on a single line.
  const showDoneInPeriodChip = variant === 'chart' || variant === 'compactChart';

  return (
    <Card ref={ref} className="h-full shadow-none">
      <CardContent className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Epic Completion by Stream
          </p>
          {summary && <EpicHeaderStats summary={summary} showDoneInPeriodChip={showDoneInPeriodChip} />}
        </div>

        {data?.orphanStoryTaskStats && data.orphanStoryTaskStats.total > 0 && (() => {
          const { orphans, total } = data.orphanStoryTaskStats;
          const pct = Math.round((orphans / total) * 100);
          const periodLabel = formatPeriodLabel(filters.pill);
          return (
            <p className="mb-3 text-[11px] text-muted-foreground">
              Stories &amp; Tasks created in{' '}
              <span className="font-medium text-foreground">{periodLabel}</span>
              {' '}without epic:{' '}
              <span className={cn('font-semibold tabular-nums', getTierColorClass(100 - pct, linkageThresholdSet))}>
                {orphans.toLocaleString()} of {total.toLocaleString()} ({pct}%)
              </span>
            </p>
          );
        })()}

        {loading && (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        )}

        {!loading && error && <ErrorBanner message={error} />}

        {!loading && !error && data && !hasData && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No epics match the current filters.
          </p>
        )}

        {!loading && !error && hasData && (
          <>
            {variant === 'chart' && <EpicCompletionChart data={data} />}
            {variant === 'compactChart' && <EpicCompletionChart data={data} compact />}
            {variant === 'list' && <EpicCompletionList data={data} />}
            {variant === 'miniList' && <EpicCompletionList data={data} minimal />}
          </>
        )}
      </CardContent>
    </Card>
  );
}
